import React from 'react';
import styled from 'styled-components';
import { Icon } from '../elements/Icon';
import { Button, Content, LeftIconWrap, LabelWrap } from '../elements/Button';
import { getColor } from '../utils/styled';
import {
  CURIOUS_BLUE,
  GRAY_40,
  PURE_BLACK,
  PURE_WHITE
} from '../refs/Colors';
import { ICON_WARNING } from '../refs/Icons';
import { Anchor, Placement } from "../elements/Anchor";

type AlertPopupProps = {
  className?: string;
  children: React.ReactNode;
  icon?: string | React.ReactNode;
  color?: string;
  cancelLabel?: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  my?: Placement;
  at?: Placement;
  'data-testid'?: string;
};

const PopupWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 21px;
  width: 311px;
  padding: 24px;
  background-color: ${getColor(PURE_WHITE)};
  border: 1px solid ${getColor(GRAY_40)};
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  & > p {
    margin: revert;
  }
`;

const IconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const ContentText = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.35;
  color: ${getColor(PURE_BLACK)};
  text-align: center;
  white-space: pre-wrap;
`;

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CancelButton = styled(Button).attrs({ variant: 'link' })``;

const ConfirmButton = styled(Button)`
  && {
    padding: 1px 0 4px;
  }

  ${Content} ${LeftIconWrap} {
    padding-left: 0;
  }

  ${Content} ${LabelWrap} {
    padding: 0 10px;
  }
`;

export const AlertPopup: React.FC<AlertPopupProps> = ({
  className,
  children,
  icon = ICON_WARNING,
  color = CURIOUS_BLUE,
  cancelLabel = "No, cancel",
  confirmLabel,
  onClose,
  onConfirm,
  my="top right",
  at="top left",
  ...rest
}) => {
  return (
    <Anchor.Popup my={my} at={at} margin="-8px" withArrow arrowGap={14}>
      <PopupWrap data-testid={rest["data-testid"] || 'alert-popup'} className={className}>
        <IconWrap>
          {typeof icon === 'string' ? (
            <Icon icon={icon} size={40} color={color} />
          ) : (
            icon
          )}
        </IconWrap>
        <ContentText>{children}</ContentText>
        <ButtonsRow>
          <CancelButton data-testid='alert-popup-cancel' type="button" onClick={onClose}>
            {cancelLabel}
          </CancelButton>
          <ConfirmButton data-testid='alert-popup-confirm' onClick={onConfirm} color={PURE_WHITE}>
            {confirmLabel}
          </ConfirmButton>
        </ButtonsRow>
      </PopupWrap>
    </Anchor.Popup>
  );
};

export default AlertPopup;